/**
 * TaxYearSection — Standalone Tax Year assignment for the Document Viewer rail.
 * First staff decision: what tax year does this document belong to?
 * Extracted from ClassificationPanel; same canonical save path (metadata PATCH).
 */
import { useState } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";

const TAX_YEARS = Array.from({ length: 2026 - 2009 }, (_, i) => String(2026 - i)); // 2026…2010

export function TaxYearSection({ docId, taxYear, taxYears, staffConfirmedFields, onRefresh }) {
  const [mode, setMode]       = useState("idle"); // idle | editing | saving
  const [selYear, setSelYear] = useState("");
  const [err, setErr]         = useState("");

  const isTaxYearConfirmed = (staffConfirmedFields || []).includes("tax_year");
  const extraYears = (taxYears || []).filter((y) => y !== taxYear);

  const startEdit = () => {
    setSelYear(isTaxYearConfirmed && !taxYear ? "__na__" : (taxYear || ""));
    setErr("");
    setMode("editing");
  };
  const cancel = () => { setMode("idle"); setErr(""); };

  const save = async () => {
    if (!selYear) return;
    setMode("saving"); setErr("");
    try {
      const payload = selYear === "__na__"
        ? { tax_year: null }   // explicit JSON null → confirmed N/A (distinguished by Pydantic model_fields_set)
        : { tax_year: selYear };
      await api.patch(`/documents-intel/${docId}/metadata`, payload);
      setMode("idle");
      onRefresh?.();
    } catch (e) {
      setErr(e.response?.data?.detail || "Save failed.");
      setMode("idle");
    }
  };

  return (
    <div className="space-y-1.5 text-xs" data-testid="tax-year-section">
      {mode === "idle" && (
        <div className="flex items-center justify-between gap-2">
          <div data-testid="tax-year-display">
            {taxYear ? (
              <span className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded text-[11px] font-medium" data-testid="tax-year-value">
                <Icons.ShieldCheck className="h-3 w-3" />{taxYear}
                {extraYears.length > 0 && <span className="text-[10px] font-normal opacity-70">+{extraYears.join(", ")}</span>}
              </span>
            ) : isTaxYearConfirmed ? (
              <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-500 px-2 py-0.5 rounded text-[11px]" data-testid="tax-year-na">
                <Icons.Ban className="h-3 w-3" />N/A — Not Tax-Year Specific
              </span>
            ) : (
              <span className="text-slate-400 text-[11px]" data-testid="tax-year-unknown">Not yet determined</span>
            )}
          </div>
          <button
            onClick={startEdit}
            className="shrink-0 text-[10px] text-slate-400 hover:text-slate-600 flex items-center gap-0.5"
            data-testid="tax-year-edit-btn"
          >
            <Icons.Pencil className="h-2.5 w-2.5" />Edit
          </button>
        </div>
      )}

      {(mode === "editing" || mode === "saving") && (
        <div className="space-y-1.5" data-testid="tax-year-edit-form">
          <select
            value={selYear}
            onChange={(e) => setSelYear(e.target.value)}
            className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
            data-testid="tax-year-select"
          >
            <option value="">— Not yet determined —</option>
            {TAX_YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
            <option value="__na__">N/A — Not Tax-Year Specific</option>
          </select>
          {err && <div className="text-[10px] text-rose-600" data-testid="tax-year-error">{err}</div>}
          <div className="flex gap-1.5">
            <button
              onClick={save}
              disabled={!selYear || mode === "saving"}
              className="flex-1 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-[10px] font-medium disabled:opacity-50 flex items-center justify-center gap-1"
              data-testid="tax-year-save-btn"
            >
              {mode === "saving" ? <><Icons.Loader2 className="h-3 w-3 animate-spin" />Saving…</> : "Confirm"}
            </button>
            <button
              onClick={cancel}
              className="flex-1 py-1 border border-slate-200 text-slate-600 rounded text-[10px] hover:bg-slate-100"
              data-testid="tax-year-cancel-btn"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
